<template>
  <div class="pp-price-card">
    <div
      v-if="salesStatusLabel"
      class="status-strip"
    >
      <div class="st">
        <span
          class="dot"
          :style="{ backgroundColor: statusColor || '#22C55E' }"
        ></span>
        <span>{{ salesStatusLabel }}</span>
      </div>
    </div>
    <div class="body">
      <h2 class="big-price">
        <template v-if="priceMin > 0">
          <span class="from">{{ t('property.heroCard.from') }}</span>
          {{ formatPrice(priceMin) }}
          <span class="cur">{{ currency }}</span>
        </template>
        <template v-else>
          {{ title }}
        </template>
      </h2>
      <div
        v-if="unitTypes.length"
        class="unit-chips"
      >
        <span
          v-for="(type, idx) in unitTypes"
          :key="idx"
          class="c"
          >{{ type }}</span
        >
      </div>
      <hr />
      <div class="rowpair">
        <span class="lbl">{{ t('property.heroCard.developer') }}</span>
        <span class="val">{{ developerName || '—' }}</span>
      </div>
      <div
        v-if="address"
        class="rowpair"
      >
        <span class="lbl">{{ t('property.heroCard.address') }}</span>
        <span class="val">{{ address }}</span>
      </div>
      <hr v-if="progressPercentage != null" />
      <div
        v-if="progressPercentage != null"
        class="progress-wrap"
      >
        <div class="lbl">{{ t('property.heroCard.completion') }}</div>
        <div class="bar">
          <div
            class="fill"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
        <div class="meta">
          <span>
            <i18n-t
              keypath="property.shared.percentCompletedRich"
              tag="span"
            >
              <template #percent
                ><strong>{{ progressPercentage }}%</strong></template
              >
            </i18n-t>
          </span>
          <span v-if="completionLabel">{{ t('property.heroCard.estimated', { date: completionLabel }) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  title: string;
  priceMin: number;
  currency: string;
  unitTypes: string[];
  developerName: string;
  address?: string;
  progressPercentage?: number | null;
  completionLabel?: string | null;
  salesStatusLabel: string;
  statusColor?: string;
}>();

const { t } = useI18n();
const { formatNumber } = useFormat();

const formatPrice = (price: number) => {
  if (!Number.isFinite(price) || price <= 0) return '—';
  return formatNumber(price, { notation: 'compact', maximumFractionDigits: 2 });
};
</script>

<style scoped lang="scss">
.pp-price-card {
  background: #fff0da;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 182, 72, 0.4);

  .status-strip {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-block: 0;
    padding-inline: 20px 22px;
    background: white;
    border-bottom: 1px solid rgba(255, 182, 72, 0.4);

    .st {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 600;
      color: #0d0f15;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }
  }

  .body {
    padding: 22px;
  }

  .big-price {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 28px;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: #0d0f15;
    margin: 0 0 10px;

    .from {
      font-family: 'Manrope', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-end: 4px;
    }

    .cur {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 14px;
      color: rgba(13, 15, 21, 0.62);
      margin-inline-start: 6px;
      font-weight: 500;
    }
  }

  .unit-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;

    .c {
      padding: 5px 10px;
      border-radius: 7px;
      background: white;
      border: 1px solid rgba(13, 15, 21, 0.08);
      font-size: 12px;
      font-weight: 600;
      color: #0d0f15;
    }
  }

  hr {
    border: none;
    border-top: 1px solid rgba(255, 182, 72, 0.45);
    margin: 14px 0;
  }

  .rowpair {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    .lbl {
      font-size: 12.5px;
      color: rgba(13, 15, 21, 0.62);
    }

    .val {
      font-size: 14px;
      font-weight: 700;
      color: #0d0f15;
      text-align: end;
    }
  }

  .progress-wrap {
    margin: 14px 0 16px;

    .lbl {
      font-size: 12.5px;
      color: rgba(13, 15, 21, 0.62);
      margin-bottom: 6px;
    }

    .bar {
      height: 8px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.12);
      overflow: hidden;

      .fill {
        height: 100%;
        background: #ffb648;
        border-radius: 999px;
      }
    }

    .meta {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-top: 6px;
      font-size: 12.5px;
      color: rgba(13, 15, 21, 0.62);

      strong {
        color: #0d0f15;
        font-weight: 700;
      }
    }
  }
}
</style>
